import axios, { type AxiosError, type  AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '../types';
import { getMsalInstance, acquireTokenForApi, forceReauth } from '../auth';

// -------------------------------------------------------------------------
// Key converters: camelCase ↔ snake_case
// -------------------------------------------------------------------------

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function convertKeysToSnake(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnake);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[camelToSnake(key)] = convertKeysToSnake(value);
    }
    return result;
  }
  return obj;
}

function convertKeysToCamel(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamel);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[snakeToCamel(key)] = convertKeysToCamel(value);
    }
    return result;
  }
  return obj;
}

// -------------------------------------------------------------------------
// Axios instance
// -------------------------------------------------------------------------

import { runtimeConfig } from '../config/runtime';

const BASE_URL = runtimeConfig.API_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// -------------------------------------------------------------------------
// Request interceptor — acquire MSAL token and attach as Bearer header.
// Falls back to localStorage for environments without Entra ID (dev/local).
// -------------------------------------------------------------------------

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Convert snake_case request body keys to camelCase for the Java backend.
    // Skip binary/multipart bodies (FormData, Blob, etc.) — converting them
    // strips the payload and forces a JSON Content-Type, breaking file uploads.
    if (
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof FormData) &&
      !(config.data instanceof Blob) &&
      !(config.data instanceof ArrayBuffer) &&
      !(config.data instanceof URLSearchParams)
    ) {
      config.data = convertKeysToCamel(config.data);
    }

    try {
      const token = await acquireTokenForApi(getMsalInstance());
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
    } catch (_msalError) {
      // MSAL not initialized or no accounts — fall through to localStorage
    }

    // Fallback for dev/local environments without Entra ID
    const localToken = localStorage.getItem('auth_token');
    if (localToken && config.headers) {
      config.headers.Authorization = `Bearer ${localToken}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// -------------------------------------------------------------------------
// Response interceptor — convert camelCase keys to snake_case, normalise errors
// -------------------------------------------------------------------------

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeysToSnake(response.data);
    }
    return response;
  },
  (error: AxiosError<ApiError | { detail: string }>) => {
    if (error.response) {
      const status = error.response.status;

      // 401 — the backend rejected our token. Trigger a single guarded
      // interactive re-auth rather than a full Entra SSO sign-out. forceReauth
      // debounces concurrent 401s (a page fires several requests at once) to
      // one redirect, avoiding the colliding-interaction lockup that previously
      // left a stuck `interaction_in_progress` flag in storage.
      if (status === 401) {
        localStorage.removeItem('auth_token');
        forceReauth(getMsalInstance());
      }

      // 403
      if (status === 403) {
        console.warn('[api] Forbidden.');
      }

      // Surface a readable message
      const serverMessage =
        (error.response.data as ApiError)?.detail ?? error.message;
      return Promise.reject(new Error(serverMessage));
    }

    if (error.request) {
      return Promise.reject(
        new Error('No response received from the server. Please check your connection.')
      );
    }

    return Promise.reject(error);
  }
);

export default apiClient;
